// E-Invoice validation rules catalog (BOL-414 / BOL-417).
// Mirrors the GET / PUT contract from
// doc2-api/routers/electronic_document/router.py.

import AbstractApiService from 'src/services/abstractApiService';
import { api } from 'pages/APIs';

export type RuleSeverity = 'fatal' | 'error' | 'warning' | 'info';

export type RuleLayer = 'L0' | 'L0b' | 'L0c' | 'L1' | 'L2' | 'L3' | 'L4';

export interface ValidationRuleRow {
  code: string;
  layer: RuleLayer;
  profile: string; // XRECHNUNG | EN16931 | PRE_XML | DEMO | ANY | …
  version: string | null;
  field: string | null;
  default_severity: RuleSeverity;
  effective_severity: RuleSeverity;
  override: RuleSeverity | null;
  // BOL-424 detail fields. Populated for L4 in-house codes (IBAN
  // family); null/empty for KoSIT-extracted L3 rules and the simpler
  // L0/L0b/L1/L2 codes where they don't apply.
  field_label: string | null;
  description: string | null;
  method: string | null;
  triggered_when: string | null;
  example_valid: string | null;
  example_invalid: string | null;
  references: string[];
  // BOL-424 rationale: every row carries a "why this rule exists"
  // string (per-rule for in-house codes, layer-fallback for KoSIT
  // codes) so admins can decide whether to override safely.
  rationale: string | null;
}

// BOL-429: per-org acceptance policy registry.
export interface VersionRegistryEntry {
  version: string; // "3.0.2" | "3.0" | "2.3" | …
  label: string;
  status: 'current' | 'deprecated' | 'eol';
  released_on: string | null;
  note: string | null;
  has_bundle: boolean;
}

export interface ProfileRegistryEntry {
  profile: 'MINIMUM' | 'BASIC_WL' | 'BASIC' | 'EN16931' | 'EXTENDED' | 'XRECHNUNG';
  label: string;
  en16931_conformant: boolean;
  note: string | null;
}

export interface ValidationVersionsResponse {
  success: boolean;
  versions: VersionRegistryEntry[];
  profiles: ProfileRegistryEntry[];
}

export interface ListFilters {
  layer?: string;
  profile?: string;
  version?: string;
}

export default class ValidationRulesApiService extends AbstractApiService {
  public constructor() {
    super({});
  }

  public async list(filters: ListFilters = {}): Promise<{
    success: boolean;
    data: ValidationRuleRow[];
  }> {
    const qs = new URLSearchParams();
    if (filters.layer) qs.append('layer', filters.layer);
    if (filters.profile) qs.append('profile', filters.profile);
    if (filters.version) qs.append('version', filters.version);
    const url =
      qs.toString().length > 0
        ? `${api.einvoice_validation_rules}?${qs.toString()}`
        : api.einvoice_validation_rules;
    return super.get(url);
  }

  /** Set or clear a per-org severity override.
   *  Pass `severity = null` (or empty) to clear and revert to KoSIT default. */
  public async setOverride(
    code: string,
    severity: RuleSeverity | null,
  ): Promise<{ success: boolean; rule_code: string; severity: string | null }> {
    const formData = new FormData();
    if (severity) formData.append('severity', severity);
    return super.put(
      `${api.einvoice_validation_rule_severity}/${encodeURIComponent(code)}/severity`,
      formData,
    );
  }

  /** Fetch the supported XRechnung version + ZUGFeRD profile registries.
   *  Drives the BOL-429 acceptance-policy section. */
  public async listVersions(): Promise<ValidationVersionsResponse> {
    return super.get(api.einvoice_validation_versions);
  }
}
